import React, { createContext, useContext, useEffect, useCallback } from "react";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import en from "./locales/en.json";
import nl from "./locales/nl.json";
import ar from "./locales/ar.json";
import { api } from "@/lib/api";

export const LANG_META = {
  en: { label: "English", dir: "ltr", locale: "en-GB" },
  nl: { label: "Nederlands", dir: "ltr", locale: "nl-BE" },
  ar: { label: "العربية", dir: "rtl", locale: "ar-u-nu-latn" },
};

export const STORAGE_KEY = "garage_lang";
const stored = localStorage.getItem(STORAGE_KEY);
const initial = LANG_META[stored] ? stored : "nl";

i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, nl: { translation: nl }, ar: { translation: ar } },
  lng: initial,
  fallbackLng: "en",
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false, prefix: "{", suffix: "}" },
  returnEmptyString: false,
});

export const applyDirection = (code) => {
  const meta = LANG_META[code] || LANG_META.nl;
  document.documentElement.setAttribute("dir", meta.dir);
  document.documentElement.setAttribute("lang", code);
  document.body.classList.toggle("font-arabic", code === "ar");
};

applyDirection(initial);
i18n.on("languageChanged", (code) => {
  localStorage.setItem(STORAGE_KEY, code);
  applyDirection(code);
});

/** Apply the language saved on the user profile (called after login / me). */
export const applyUserLanguage = (user) => {
  const code = user?.language;
  if (code && LANG_META[code] && code !== i18n.language) i18n.changeLanguage(code);
};

const LangCtx = createContext(null);

export function LanguageProvider({ children }) {
  const { t, i18n: inst } = useTranslation();
  const lang = LANG_META[inst.language] ? inst.language : "nl";

  useEffect(() => { applyDirection(lang); }, [lang]);

  const setLang = useCallback((code) => {
    if (!LANG_META[code]) return;
    inst.changeLanguage(code);
    if (localStorage.getItem("garage_session")) {
      api.patch("/auth/me/language", { language: code }).catch(() => {});
    }
  }, [inst]);

  return (
    <LangCtx.Provider value={{ lang, setLang, t, meta: LANG_META[lang], langs: LANG_META }}>
      <React.Fragment key={lang}>{children}</React.Fragment>
    </LangCtx.Provider>
  );
}

export const useLang = () => useContext(LangCtx);
/** Non-hook translator for module-level code; components remount on language change. */
export const tr = (k, vars) => i18n.t(k, vars);
export default i18n;
